import type { ColumnDef } from '@tanstack/react-table'
import type { IPOI } from '@/shared/api/types/poi-types'
import type { PaginationConfigInput } from '@/shared/ui/data-table'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { PoiDeleteButton } from '@/features/poi/poi-delete-button'
import { PoiStatusToggle } from '@/features/poi/poi-status-toggle'
import { formatDateTime } from '@/shared/api/utils/helpers'
import { POI_CREATED_SOURCE } from '@/shared/constants/poi-created-source'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar'
import { Badge } from '@/shared/ui/badge'
import { DataTable } from '@/shared/ui/data-table'
import { resolveDashboardLocalizedValue } from '@/shared/utils/dashboard-localized-value'
import { resolvePoiCategoryName } from '@/shared/utils/poi-category-utils'

interface PoiListTableProps {
  pois: IPOI[]
  loading: boolean
  pagination: PaginationConfigInput
  languageCode: string
  onPageChange: (page: number, pageSize: number) => void
  onEdit: (poi: IPOI) => void
}

/**
 * Displays POIs with localized names, status controls, and pagination.
 */
export function PoiListTable({
  pois,
  loading,
  pagination,
  languageCode,
  onPageChange,
  onEdit,
}: PoiListTableProps) {
  const { t } = useTranslation()
  const columns = useMemo<ColumnDef<IPOI>[]>(
    () => [
      {
        id: 'poi',
        header: t('poi.name'),
        cell: ({ row }) => {
          const name = resolveDashboardLocalizedValue(row.original.localizedData, languageCode)
          const area = row.original.destinationName?.trim()
          return (
            <div className="flex min-w-[240px] items-center gap-3">
              <Avatar className="h-11 w-11 rounded-md">
                <AvatarImage src={row.original.banner[0]} alt={name} className="object-cover" />
                <AvatarFallback className="rounded-md">
                  {name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-zinc-900">
                  {name || t('poi.noName')}
                </p>
                <p className="truncate text-xs text-zinc-500">
                  {area && area.length > 0
                    ? area
                    : t('poi.noDestination', { defaultValue: 'Chưa gán điểm đến' })}
                </p>
              </div>
            </div>
          )
        },
      },
      {
        id: 'category',
        header: t('poi.category'),
        cell: ({ row }) => {
          const category = row.original.categoryData
          const categoryName = resolvePoiCategoryName(category, [languageCode])
          return (
            <span className="inline-flex max-w-[180px] items-center gap-1.5 rounded border border-zinc-200 bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700">
              {category?.icon && (
                <img
                  src={category.icon}
                  alt=""
                  className="h-4 w-4 shrink-0 rounded-sm object-cover"
                  loading="lazy"
                />
              )}
              <span className="truncate">{categoryName || '—'}</span>
            </span>
          )
        },
      },
      {
        id: 'type',
        header: t('poi.type', { defaultValue: 'Type' }),
        // * VI: Dùng chung key `poi.types.*` với form chi tiết để bảng không hiện mã thô "major".
        // * EN: Reuse the same `poi.types.*` keys as the detail form so the table stops showing the raw "major" code.
        cell: ({ row }) =>
          row.original.type
            ? t(`poi.types.${row.original.type}`, { defaultValue: row.original.type })
            : '—',
      },
      {
        id: 'createdSource',
        header: t('poi.createdSource'),
        cell: ({ row }) => (
          <Badge
            variant={
              row.original.createdSource === POI_CREATED_SOURCE.CRAWL_DATA_SYSTEM
                ? 'secondary'
                : 'outline'
            }
          >
            {t(
              `poi.createdSourceOptions.${row.original.createdSource === POI_CREATED_SOURCE.CRAWL_DATA_SYSTEM ? 'crawlDataSystem' : 'tenantAdmin'}`,
            )}
          </Badge>
        ),
      },
      {
        id: 'updatedAt',
        header: t('common.updatedAt', { defaultValue: 'Updated at' }),
        cell: ({ row }) => formatDateTime(row.original.updatedAt),
      },
      {
        id: 'status',
        header: () => <div className="text-center">{t('common.status')}</div>,
        cell: ({ row }) => (
          <PoiStatusToggle poiId={row.original.id} isActive={row.original.isActive} />
        ),
      },
      {
        id: 'actions',
        size: 64,
        header: () => null,
        cell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <PoiDeleteButton poiId={row.original.id} />
          </div>
        ),
      },
    ],
    [t, languageCode],
  )

  return (
    <DataTable
      columns={columns}
      data={pois}
      loading={loading}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        onChange: onPageChange,
      }}
      onRowClick={onEdit}
    />
  )
}
